import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '../../../lib/utils';
import {
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  tertiaryText,
  baseTextSize,
  borderPrimary,
  darkGreenTextColor,
} from '../../../styling/stylingHelper';
import { currencyFormatter as currencyFmt } from '../../../services/helperService';
import { Platform } from '../../../types/Platforms';
import { isKnownPlatform } from '../costPrice/review/reviewUtils';
import { SupplierLogo } from '../../ui/SupplierLogo';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import { useInventoryStore } from '../../../stores/appInventoryStore';
import { useAuth } from '../../../services/authentication/authContext';
import { canAccessInventoryPage, canAccessInventoryImport } from '../../../services/accessManagement/accessManagement';
import { useLoadInventorySections } from '../../../utils/dataLoadingUtils';
import { DeleteConfirmationModal } from '../../common/DeleteConfirmationModal';
import { InventoryGroup, InventorySection, InventorySectionProduct, GroupNotEmptyError } from '../../../services/inventory/inventoryService';
import SaveInventorySectionModal from './SaveInventorySectionModal';
import SaveInventoryGroupModal from './SaveInventoryGroupModal';
import AssignProductsModal from './AssignProductsModal';
import EditProductUnitModal from './EditProductUnitModal';
import ReLinkInventoryProductModal, { RelinkTarget } from './ReLinkInventoryProductModal';
import RenameInventoryProductModal, { RenameTarget } from './RenameInventoryProductModal';
import ManageInventoryCategoriesModal from './ManageInventoryCategoriesModal';
import ImportInventoryExcelModal from './ImportInventoryExcelModal';
import SetInventoryPriceModal, { SetPriceTarget } from './SetInventoryPriceModal';
import { InventoryProductSearchInput } from './inventoryProductMobileCard';
import { SortableList } from '../../common/SortableList';

interface MenuItem {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

interface ActionMenuProps {
  items: MenuItem[];
  menuId: string;
  openMenuId: string | null;
  onOpenChange: (id: string | null) => void;
}

const ActionMenu = ({ items, menuId, openMenuId, onOpenChange }: ActionMenuProps) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const isOpen = openMenuId === menuId;

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onOpenChange(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, onOpenChange]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpenChange(isOpen ? null : menuId);
        }}
        className="w-6 h-6 flex items-center justify-center"
        aria-label={t('inventory.actions')}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className='dark:text-[#CBD5E1] text-[#475467]' xmlns="http://www.w3.org/2000/svg">
          <rect x="10.6504" y="4.12402" width="2.7" height="2.7" rx="1" stroke="currentColor" strokeWidth="2" />
          <rect x="10.6504" y="10.874" width="2.7" height="2.7" rx="1" stroke="currentColor" strokeWidth="2" />
          <rect x="10.6504" y="17.624" width="2.7" height="2.7" rx="1" stroke="currentColor" strokeWidth="2" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute right-0 top-full mt-1 z-50 min-w-[140px] sm:min-w-[200px] bg-white dark:bg-[#13354F] rounded-xl border border-[#F5F5F5] dark:border-[#2D4F68] shadow-[0px_8px_10px_0px_#00000012] dark:shadow-[0px_8px_10px_0px_#00000040] sm:py-1 sm:px-2 overflow-hidden">
          {items.map((item, idx) => (
            <button
              key={idx}
              type="button"
              disabled={item.disabled}
              onClick={(e) => {
                e.stopPropagation();
                onOpenChange(null);
                item.onClick();
              }}
              className={cn(
                'w-full text-nowrap flex items-center gap-2 p-3 text-[14px] sm:text-[16px] leading-5 sm:leading-7 font-medium transition-colors text-left border-b border-[#EAECF0] dark:border-[#FFFFFF14] last:border-none',
                item.disabled && 'opacity-40 cursor-not-allowed',
                item.destructive
                  ? 'text-[#D92D20] dark:text-[#F87171]'
                  : 'text-[#344054] dark:text-[#F8FAFC]'
              )}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/** Column widths shared by the product rows and their header strip so they line up.
 *  Price/category shrink with the window — fixed widths on every column made the row
 *  overflow its card on narrow screens. */
const COL_SUPPLIER = 'w-12 sm:w-14 flex-none';
const COL_UNIT = 'w-[112px] sm:w-[150px] flex-none';
const COL_MIN = 'w-[72px] sm:w-[92px] flex-none';
const COL_PRICE = 'flex-1 min-w-[64px]';
const COL_CATEGORY = 'basis-[112px] sm:basis-[136px] min-w-[80px] shrink';

const cellInputClass = cn(
  'px-2 h-10 sm:h-8 rounded-lg border bg-white dark:bg-[#0E2B42] text-[12px] sm:text-[14px] leading-5 text-[#344054] dark:text-[#CBD5E1]',
  'focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed',
  borderPrimary
);

const ProductColumnsHeader = ({ hasCategories, t }: { hasCategories: boolean; t: (key: string) => string }) => (
  <div className="hidden sm:flex items-center gap-3 px-1 pb-2 text-[12px] leading-4 font-medium text-[#667085] dark:text-[#CBD5E1] uppercase tracking-wide">
    <span className="w-5 flex-none" />
    <span className="w-4 flex-none" />
    <span className="flex-1 min-w-0">{t('inventory.product')}</span>
    <span className={COL_SUPPLIER}>{t('inventory.colSupplier')}</span>
    <span className={COL_UNIT}>{t('inventory.colUnit')}</span>
    <span className={cn(COL_MIN, 'truncate')}>{t('inventory.colMinimum')}</span>
    <span className={cn(COL_PRICE, 'truncate')}>{t('inventory.colPrice')}</span>
    {hasCategories && <span className={cn(COL_CATEGORY, 'truncate')}>{t('inventory.category')}</span>}
    <span className="w-6 flex-none" />
  </div>
);

/** Trim float noise off a factor (1/(1/750) → 750, not 750.0000000001) without locale separators. */
const formatFactor = (value: number): string => String(Number(value.toFixed(6)));

interface ProductRowProps {
  product: InventorySectionProduct;
  selected: boolean;
  onToggleSelected: () => void;
  sectionId: string;
  sortedCategories: { id: string; name: string }[];
  /** Reordering writes SECTION-WIDE positions from the rendered rows, so it is switched
   *  off while a search hides part of the section (see handleReorderBucket). */
  dragDisabled: boolean;
  onSetCategory: (productId: string, sectionId: string, categoryId: string | null) => void;
  /** null clears the minimum. Rejects → the row keeps the value it had. */
  onSetMinimum: (minStockCount: number | null) => Promise<unknown>;
  onEditUnit: () => void;
  onEditLink: () => void;
  onSetPrice: () => void;
  onRename: () => void;
  onRemove: () => void;
  menuId: string;
  openMenuId: string | null;
  onOpenMenuChange: (id: string | null) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

const SortableProductRow = ({ product: p, selected, onToggleSelected, sectionId, sortedCategories, dragDisabled, onSetCategory, onSetMinimum, onEditUnit, onEditLink, onSetPrice, onRename, onRemove, menuId, openMenuId, onOpenMenuChange, t }: ProductRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.id, disabled: dragDisabled });
  const style: React.CSSProperties = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const pricePer = p.pricePerCountUnit;
  const extraUnits = p.additionalCountUnits || [];
  // Minimum: read-only text until clicked, then an input that saves on blur. `null` = not editing.
  const [minDraft, setMinDraft] = useState<string | null>(null);
  const [savingMin, setSavingMin] = useState(false);
  // Set by Escape so a blur firing right after it can't still write the abandoned draft.
  const abandonMin = useRef(false);

  // Custom ingredients without a named supplier carry the 'unsupported' sentinel — not a supplier name.
  const supplier = p.platform && p.platform.toLowerCase() !== 'unsupported' ? p.platform : null;
  const supplierCell = (
    <div className={cn(COL_SUPPLIER, 'flex-none flex items-center')}>
      {supplier && isKnownPlatform(supplier) ? (
        <SupplierLogo
          platform={supplier.toLowerCase() as Platform}
          size="small"
          className="h-4 max-w-[52px] object-contain"
          alt={supplier}
          title={supplier}
        />
      ) : (
        <span className="text-[12px] leading-4 text-[#667085] dark:text-[#CBD5E1] truncate" title={supplier || undefined}>
          {supplier || '—'}
        </span>
      )}
    </div>
  );

  // Fully read-only: the whole conversion ("fles = 750 ml") plus the extra count units by
  // name. Editing any of it happens in the count-units slide-over, which a click opens.
  // Cross-dimension piece units store a fractional factor (1 p = 750 g ⇒ 1/750) — those read
  // from the std unit instead, the same flip the slide-over shows.
  const isPieceStd = p.stdUnit === 'p' || p.stdUnit === 'unit';
  const inverseFactor = isPieceStd && p.countUnitToStdFactor > 0 && p.countUnitToStdFactor < 1;
  const shownFactor = inverseFactor ? 1 / p.countUnitToStdFactor : p.countUnitToStdFactor;
  const conversionLabel =
    !inverseFactor && p.countUnitToStdFactor === 1 && p.countUnit.trim().toLowerCase() === p.stdUnit.toLowerCase()
      ? p.countUnit
      : `${inverseFactor ? p.stdUnit : p.countUnit} = ${formatFactor(shownFactor)} ${inverseFactor ? p.countUnit : p.stdUnit}`;

  const unitCell = (
    <button
      type="button"
      onClick={onEditUnit}
      title={extraUnits.length > 0
        ? `${t('inventory.moreUnits')}: ${extraUnits.map((u) => u.unit).join(', ')}`
        : t('inventory.primaryUnitHint')}
      className={cn(COL_UNIT, 'flex flex-col items-start text-left text-[12px] sm:text-[14px] leading-5 text-[#344054] dark:text-[#CBD5E1] hover:opacity-80')}
    >
      <span className="max-w-full truncate">{conversionLabel}</span>
      {extraUnits.length > 0 && (
        <span className="max-w-full truncate text-[12px] leading-4 font-medium text-[#0E8965] dark:text-[#28A878]">
          + {extraUnits.map((u) => u.unit).join(', ')}
        </span>
      )}
    </button>
  );

  // Minimum stock, edited straight in the row: click → input, blur → save, no save button.
  // It is a restaurant-wide total across sections (M63), entered in this row's count unit.
  const commitMin = () => {
    if (abandonMin.current) { abandonMin.current = false; setMinDraft(null); return; }
    const raw = (minDraft ?? '').trim().replace(',', '.');
    setMinDraft(null);
    const current = p.minStockCount ?? null;
    let next: number | null;
    if (raw === '') {
      next = null;
    } else {
      const parsed = Number(raw);
      // Junk or a non-positive number isn't a minimum — drop it, keep what was there.
      if (!Number.isFinite(parsed) || parsed <= 0) return;
      next = parsed;
    }
    if (next === current) return;
    setSavingMin(true);
    onSetMinimum(next)
      .catch((err) => console.error('Failed to save inventory minimum:', err))
      .finally(() => setSavingMin(false));
  };

  const minCell = (
    <div className={cn(COL_MIN, 'flex items-center')}>
      {minDraft === null ? (
        <button
          type="button"
          onClick={() => {
            // Clear a flag left over from an Escape that never produced a blur.
            abandonMin.current = false;
            setMinDraft(p.minStockCount != null ? String(p.minStockCount) : '');
          }}
          disabled={savingMin}
          title={t('inventory.minimumHint', { unit: p.minStockCountUnit || p.countUnit })}
          className="w-full text-left truncate text-[12px] sm:text-[14px] leading-5 text-[#344054] dark:text-[#CBD5E1] hover:opacity-80"
        >
          {p.minStockCount != null
            ? `${formatFactor(p.minStockCount)} ${p.minStockCountUnit || p.countUnit}`
            : <span className="text-[#98A2B3] dark:text-[#94A3B8]">{t('inventory.minimumNotSet')}</span>}
        </button>
      ) : (
        <input
          type="number"
          inputMode="decimal"
          min="0"
          step="any"
          autoFocus
          value={minDraft}
          onChange={(e) => setMinDraft(e.target.value)}
          onBlur={commitMin}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            // Escape abandons the edit — the flag makes that stick whether or not the
            // unmounting input still emits a blur.
            if (e.key === 'Escape') { abandonMin.current = true; setMinDraft(null); }
          }}
          onWheel={(e) => e.currentTarget.blur()}
          placeholder={p.countUnit}
          title={t('inventory.minimumHint', { unit: p.countUnit })}
          className={cn(cellInputClass, 'w-full min-w-0')}
        />
      )}
    </div>
  );

  // A product inventory can't value (no price, or a €0 invoice line) renders a blank
  // price here — show it explicitly and offer the durable fix inline. A subrecipe is
  // excluded: its price is derived from its own ingredients, so a durable override here
  // would silently disagree with the subrecipe page (the server rejects it too).
  const priceCell = (
    <div className={cn(COL_PRICE, 'text-[12px] sm:text-[14px] leading-5 text-[#667085] dark:text-[#CBD5E1] truncate')}>
      {pricePer ? (
        <span>{currencyFmt.format(pricePer)} / {p.countUnit}</span>
      ) : p.source === 'subrecipe' ? (
        <span title={t('inventory.subrecipePriceHint')} className="text-[#98A2B3] dark:text-[#94A3B8]">
          {t('inventory.badgeNoPrice')}
        </span>
      ) : (
        <button
          type="button"
          onClick={onSetPrice}
          className="underline underline-offset-2 text-amber-700 dark:text-amber-300 hover:opacity-80"
        >
          {t('inventory.setPriceAction')}
        </button>
      )}
    </div>
  );

  return (
    <li
      ref={setNodeRef}
      style={style}
      onClick={(e) => {
        // Editing controls and the drag handle keep their own click behavior.
        if ((e.target as Element).closest('button, input, select, a')) return;
        onToggleSelected();
      }}
      className={cn(
        'border-b border-[#EAECF0] dark:border-[#1E3A4E] last:border-none cursor-pointer',
        selected && 'bg-green-50 dark:bg-green-900/30'
      )}
    >
      <div className="flex items-center gap-2 sm:gap-3 px-1 py-2">
        <button
          {...attributes}
          {...listeners}
          type="button"
          disabled={dragDisabled}
          title={dragDisabled ? t('inventory.reorderDisabledWhileSearching') : undefined}
          className={cn(
            'flex-none w-5 text-[#475467] dark:text-[#CBD5E1]',
            dragDisabled
              ? 'opacity-30 cursor-not-allowed'
              : 'touch-none cursor-grab active:cursor-grabbing'
          )}
          aria-label={t('inventory.dragToReorder')}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_389_17963)">
              <path d="M7 3.02441H7.00476M7 10.2244H7.00476M7 17.4244H7.00476M12.9952 3.02441H13M12.9952 10.2244H13M12.9952 17.4244H13" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </g>
            <defs>
              <clipPath id="clip0_389_17963">
                <rect width="20" height="20" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </button>
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelected}
          aria-label={t('inventory.selectSectionProduct', { name: p.productName })}
          className="h-4 w-4 flex-none cursor-pointer accent-green-600 dark:accent-green-400"
        />
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <span className="text-[14px] sm:text-[16px] leading-5 font-semibold text-[#101828] dark:text-[#F8FAFC] truncate">{p.productName}</span>
          {p.source === 'custom' && (
            <span className="flex-none text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
              {t('inventory.customBadge')}
            </span>
          )}
          {p.source === 'subrecipe' && (
            <span className="flex-none text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200">
              {t('inventory.subrecipeBadge')}
            </span>
          )}
        </div>
        {supplierCell}
        <div className="hidden sm:contents">
          {unitCell}
          {minCell}
          {priceCell}
        </div>
        {sortedCategories.length > 0 && (
          <select
            value={p.inventoryCategoryId ?? ''}
            onChange={(e) => onSetCategory(p.id, sectionId, e.target.value || null)}
            title={t('inventory.category')}
            className={cn(cellInputClass, COL_CATEGORY)}
          >
            <option value="">{t('inventory.categoryNone')}</option>
            {sortedCategories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}
      <ActionMenu
        menuId={menuId}
        openMenuId={openMenuId}
        onOpenChange={onOpenMenuChange}
        items={[
          {
            label: t('inventory.editUnit'),
            icon: <svg width="20" height="20" viewBox="0 0 20 20" className='dark:text-[#CBD5E1] text-[#141B34]' fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.8493 6.48826L11.4878 10.8498C10.6167 11.7209 10.1811 12.1564 9.65038 12.453C9.11963 12.7496 7.86637 13.048 6.66797 13.3333C6.9533 12.1349 7.2517 10.8817 7.54828 10.3509C7.84486 9.82018 8.28041 9.38463 9.15149 8.51354L13.513 4.15199L14.6812 2.98386C15.3263 2.33871 16.3723 2.33871 17.0174 2.98386C17.6626 3.629 17.6626 4.67498 17.0174 5.32013L15.8493 6.48826ZM13.513 4.15199L15.8493 6.48826" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M17.5 10C17.5 13.5355 17.5 15.3033 16.4016 16.4016C15.3033 17.5 13.5355 17.5 10 17.5C6.46447 17.5 4.6967 17.5 3.59835 16.4016C2.5 15.3033 2.5 13.5355 2.5 10C2.5 6.46447 2.5 4.6967 3.59835 3.59835C4.6967 2.5 6.46447 2.5 10 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>,
            onClick: onEditUnit,
          },
          {
            label: t('inventory.renameProduct'),
            icon: <svg width="20" height="20" viewBox="0 0 20 20" className='dark:text-[#CBD5E1] text-[#141B34]' fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.33301 15.8335H16.6663" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M11.083 3.31161L11.6809 2.71372C12.6716 1.72303 14.2779 1.72303 15.2686 2.71372C16.2593 3.70441 16.2593 5.31069 15.2686 6.30138L14.6707 6.89927M11.083 3.31161C11.083 3.31161 11.1577 4.58208 12.279 5.70334C13.4002 6.8246 14.6707 6.89927 14.6707 6.89927M11.083 3.31161L5.58601 8.80861C5.21367 9.18095 5.0275 9.36712 4.86738 9.57241C4.67852 9.81456 4.51661 10.0765 4.38455 10.3536C4.27259 10.5885 4.18933 10.8383 4.02281 11.3378L3.31719 13.4547M14.6707 6.89927L9.17368 12.3963C8.80134 12.7686 8.61517 12.9548 8.40988 13.1149C8.16773 13.3038 7.90577 13.4657 7.62872 13.5977C7.39378 13.7097 7.144 13.793 6.64445 13.9595L4.52764 14.6651M4.52764 14.6651L4.01013 14.8376C3.76428 14.9196 3.49321 14.8556 3.30996 14.6723C3.12671 14.4891 3.06272 14.218 3.14468 13.9722L3.31719 13.4547M4.52764 14.6651L3.31719 13.4547" stroke="currentColor" strokeWidth="1.5" />
            </svg>,
            onClick: onRename,
          },
          {
            label: t('inventory.editLink'),
            icon: <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className='dark:text-[#CBD5E1] text-[#141B34]' xmlns="http://www.w3.org/2000/svg">
              <path d="M10.4912 8.27068C10.7424 8.41375 10.9779 8.5934 11.1923 8.80773C12.4798 10.0953 12.5151 12.1475 10.8953 13.7674L8.70148 15.9612C7.08164 17.581 5.02936 17.5457 3.74182 16.2582C2.45429 14.9706 2.41901 12.9184 4.03882 11.2985L5.63599 9.69481" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M9.50929 11.7292C9.2581 11.5862 9.02257 11.4066 8.80821 11.1922C7.52068 9.90463 7.48543 7.85239 9.10524 6.23258L11.299 4.03879C12.9189 2.41895 14.9711 2.45423 16.2587 3.74176C17.5462 5.02933 17.5815 7.08157 15.9617 8.70139L14.3645 10.3051" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
            </svg>,
            onClick: onEditLink,
          },
          {
            label: t('inventory.removeFromSection'),
            icon: <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className='dark:text-[#F87171] text-[#D92D20]' xmlns="http://www.w3.org/2000/svg">
              <path d="M16.25 4.5835L15.7336 12.9377C15.6016 15.0722 15.5356 16.1394 15.0006 16.9067C14.7361 17.2861 14.3955 17.6062 14.0006 17.8468C13.2018 18.3335 12.1325 18.3335 9.99395 18.3335C7.8526 18.3335 6.78192 18.3335 5.98254 17.8459C5.58733 17.6049 5.24666 17.2842 4.98224 16.9042C4.4474 16.1357 4.38288 15.0669 4.25384 12.9295L3.75 4.5835" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M2.5 4.58317H17.5M13.3798 4.58317L12.8109 3.40961C12.433 2.63005 12.244 2.24027 11.9181 1.99718C11.8458 1.94325 11.7692 1.89529 11.6892 1.85375C11.3283 1.6665 10.8951 1.6665 10.0288 1.6665C9.14069 1.6665 8.69665 1.6665 8.32974 1.8616C8.24842 1.90484 8.17082 1.95475 8.09774 2.01081C7.76803 2.26375 7.58386 2.6678 7.2155 3.47589L6.71077 4.58317" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M7.91602 13.75L7.91602 8.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M12.084 13.75L12.084 8.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>,
            onClick: onRemove,
            destructive: true,
          },
        ]}
      />
      </div>

      {/* Mobile: unit / minimum / price move to a second line, aligned under the name. */}
      <div className="sm:hidden flex flex-wrap items-center gap-2 pl-[52px] pb-2">
        {unitCell}
        {minCell}
        {priceCell}
      </div>
    </li>
  );
};

const InventoryEditPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { backendUser, accountSettings } = useAuth();
  const selectedRestaurant = useAppUserDefaultStore((s) => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);

  const hasAccess = canAccessInventoryPage(backendUser, accountSettings);
  const canImport = canAccessInventoryImport(backendUser, accountSettings);

  useLoadInventorySections();

  const {
    groups,
    sections,
    categories,
    sectionProducts,
    isLoadingSections,
    loadSectionProducts,
    loadSections,
    deleteSection,
    deleteGroup,
    removeProduct,
    removeProducts,
    updateProductPositions,
    setProductCategory,
    setProductMinimum,
  } = useInventoryStore();

  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<InventoryGroup | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<InventoryGroup | null>(null);
  const [createSectionForGroupId, setCreateSectionForGroupId] = useState<string | null>(null);
  const [editingSection, setEditingSection] = useState<InventorySection | null>(null);
  const [deletingSection, setDeletingSection] = useState<InventorySection | null>(null);
  const [assignTo, setAssignTo] = useState<InventorySection | null>(null);
  const [editingUnitFor, setEditingUnitFor] = useState<{ sectionId: string; product: InventorySectionProduct } | null>(null);
  const [manageCategoriesOpen, setManageCategoriesOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [priceFor, setPriceFor] = useState<SetPriceTarget | null>(null);
  const [relinkFor, setRelinkFor] = useState<RelinkTarget | null>(null);
  const [renameFor, setRenameFor] = useState<RenameTarget | null>(null);
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [removingFrom, setRemovingFrom] = useState<{ section: InventorySection; productIds: string[] } | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const removalInProgress = useRef(false);
  const [removalError, setRemovalError] = useState<{ sectionId: string; count: number } | null>(null);

  useEffect(() => {
    setSelectedProductIds(new Set());
    setRemovingFrom(null);
    setRemovalError(null);
  }, [restaurantId]);

  useEffect(() => {
    sections.forEach((s) => {
      if (!sectionProducts[s.id]) {
        loadSectionProducts(s.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections.length]);

  if (!hasAccess) return <Navigate to="/" />;
  if (isAllRestaurants) return <Navigate to="/inventory" />;
  if (!restaurantId) return null;

  // Search is purely client-side: the effect above loads EVERY section's products into the
  // store regardless of expansion, so filtering them here finds a product without the user
  // opening each section by hand. Matching follows the all-products page's predicate (name
  // or supplier) so both inventory search boxes behave identically.
  const query = search.trim().toLowerCase();
  const isSearching = query.length > 0;
  const matchesSearch = (p: InventorySectionProduct) => {
    if (p.productName.toLowerCase().includes(query)) return true;
    // The 'unsupported' sentinel is not a supplier name — the row hides it, so it can't match.
    const supplier = p.platform && p.platform.toLowerCase() !== 'unsupported' ? p.platform : '';
    return supplier.toLowerCase().includes(query);
  };
  const visibleProducts = (sectionId: string) => {
    const all = sectionProducts[sectionId] || [];
    return isSearching ? all.filter(matchesSearch) : all;
  };

  // A search forces every match open, so the collapse controls have nothing to say while one
  // is active — they no-op and render dimmed rather than looking clickable and doing nothing.
  const toggleSection = (sectionId: string) => {
    if (isSearching) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const toggleGroup = (groupId: string) => {
    if (isSearching) return;
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const catKey = (sectionId: string, bucketId: string | null) => `${sectionId}:${bucketId ?? '__none__'}`;
  const toggleCategory = (sectionId: string, bucketId: string | null) => {
    if (isSearching) return;
    const key = catKey(sectionId, bucketId);
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleDeleteSection = async () => {
    if (!deletingSection) return;
    try {
      await deleteSection(deletingSection.id, restaurantId);
    } catch (err) {
      console.error('Failed to delete inventory section:', err);
    } finally {
      setDeletingSection(null);
    }
  };

  const toggleProductSelection = (productId: string) => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const handleRemoveSelected = async () => {
    if (!removingFrom || removalInProgress.current) return;
    removalInProgress.current = true;
    setIsRemoving(true);
    setRemovalError(null);
    try {
      const failedIds = await removeProducts(removingFrom.productIds, removingFrom.section.id, restaurantId);
      if (useAppUserDefaultStore.getState().restaurant?.id !== restaurantId) return;
      const removedIds = new Set(removingFrom.productIds.filter(id => !failedIds.includes(id)));
      setSelectedProductIds(prev => new Set(Array.from(prev).filter(id => !removedIds.has(id))));
      if (failedIds.length > 0) {
        setRemovalError({ sectionId: removingFrom.section.id, count: failedIds.length });
      }
      setRemovingFrom(null);
    } finally {
      removalInProgress.current = false;
      setIsRemoving(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!deletingGroup) return;
    try {
      await deleteGroup(deletingGroup.id, restaurantId);
    } catch (err) {
      if (err instanceof GroupNotEmptyError) {
        console.warn('Cannot delete a group that still has sections');
      } else {
        console.error('Failed to delete inventory group:', err);
      }
    } finally {
      setDeletingGroup(null);
    }
  };

  const sortedGroups = [...groups].sort((a, b) => a.position - b.position || a.createdAt.getTime() - b.createdAt.getTime());
  const sortedCategories = [...categories].sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));

  // While searching, everything holding a match renders open. This is DERIVED, never written
  // into the collapse state, so clearing the search restores what the user had open by hand.
  const sectionIsOpen = (sectionId: string) => (isSearching ? true : expanded.has(sectionId));
  const groupIsCollapsed = (groupId: string) => (isSearching ? false : collapsedGroups.has(groupId));
  const categoryIsCollapsed = (sectionId: string, bucketId: string | null) =>
    isSearching ? false : collapsedCategories.has(catKey(sectionId, bucketId));

  const groupProductsByCategory = (products: InventorySectionProduct[]) => {
    const byCategory = new Map<string | null, InventorySectionProduct[]>();
    for (const p of products) {
      const key = p.inventoryCategoryId ?? null;
      const list = byCategory.get(key) ?? [];
      list.push(p);
      byCategory.set(key, list);
    }
    const buckets: { id: string | null; name: string; products: InventorySectionProduct[] }[] = [];
    for (const c of sortedCategories) {
      const list = byCategory.get(c.id);
      if (list && list.length > 0) buckets.push({ id: c.id, name: c.name, products: list });
    }
    const uncategorised = byCategory.get(null);
    if (uncategorised && uncategorised.length > 0) {
      buckets.push({ id: null, name: t('inventory.uncategorised'), products: uncategorised });
    }
    return buckets;
  };

  const sectionsForGroup = (groupId: string) =>
    sections
      .filter((s) => s.groupId === groupId)
      .filter((s) => !isSearching || visibleProducts(s.id).length > 0)
      .sort((a, b) => a.position - b.position || a.createdAt.getTime() - b.createdAt.getTime());

  const productCountForGroup = (groupId: string) =>
    sectionsForGroup(groupId).reduce((sum, s) => sum + visibleProducts(s.id).length, 0);

  const categoryCountForSection = (products: InventorySectionProduct[]) => {
    const cats = new Set(products.map((p) => p.inventoryCategoryId ?? '__none__'));
    return cats.size;
  };

  const handleReorderBucket = (
    sectionId: string,
    buckets: { id: string | null; products: InventorySectionProduct[] }[],
    bucketId: string | null,
    reordered: InventorySectionProduct[]
  ) => {
    const all = buckets.flatMap((b) => (b.id === bucketId ? reordered : b.products));
    updateProductPositions(
      sectionId,
      all.map((p, index) => ({ id: p.id, position: index }))
    ).catch((err) => console.error('Failed to reorder inventory products:', err));
  };

  // A group with no matching section disappears while searching (its own name is not matched —
  // this search is about finding a PRODUCT).
  const visibleGroups = isSearching
    ? sortedGroups.filter((g) => sectionsForGroup(g.id).length > 0)
    : sortedGroups;
  // Products stream in per section after mount, so a search typed immediately can legitimately
  // find nothing yet — say "loading", not "no results", until every section has answered.
  const sectionsAwaitingProducts = sections.some((s) => !sectionProducts[s.id]);

  const renderSection = (section: InventorySection) => {
    const isExpanded = sectionIsOpen(section.id);
    const products = visibleProducts(section.id);
    const buckets = groupProductsByCategory(products);
    const productCount = products.length;
    const categoryCount = categoryCountForSection(products);
    const totalProductCount = sectionProducts[section.id]?.length ?? 0;
    const selectedIds = (sectionProducts[section.id] || []).filter(p => selectedProductIds.has(p.id)).map(p => p.id);

    return (
      <div
        key={section.id}
        className="rounded-xl bg-white dark:bg-[#0E2B42] shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]"
      >
        <div className="flex sm:flex-row flex-col-reverse items-start sm:items-center gap-4 p-3 sm:pt-5 sm:px-5 sm:pb-4">
          <div
            className={cn('flex-1 min-w-0', !isSearching && 'cursor-pointer')}
            onClick={() => toggleSection(section.id)}
          >
            <h3 className="text-[14px] sm:text-[20px] leading-5 font-semibold text-[#101828] dark:text-zinc-50">{section.sectionName}</h3>
            <p className="text-[12px] sm:text-[14px] leading-[18px] sm:leading-5 font-medium text-[#667085] dark:text-[#CBD5E1] mt-2 sm:mt-3">
              {isSearching
                ? t('inventory.sectionSearchSummary', { matches: productCount, total: totalProductCount })
                : t('inventory.sectionSummary', { products: productCount, categories: categoryCount })}
            </p>
          </div>

          <div className="w-full sm:w-fit flex flex-wrap items-center justify-between gap-3 shrink-0">
            {selectedIds.length > 0 && (
              <button
                type="button"
                disabled={isRemoving}
                onClick={() => setRemovingFrom({ section, productIds: selectedIds })}
                className="text-[12px] sm:text-[14px] leading-5 font-medium px-1.5 py-1 sm:p-[7px] rounded-lg border border-red-600 dark:border-red-400 text-red-600 dark:text-red-400 bg-white dark:bg-transparent hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
              >
                {t('inventory.removeSelectedFromSection', { count: selectedIds.length })}
              </button>
            )}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setAssignTo(section); }}
              className={cn(
                darkGreenTextColor,
                'sm:text-[14px] dark:text-[#28A878] text-[12px] leading-5 font-medium px-1.5 py-[4px] sm:p-[7px] rounded-lg border border-[#0E8965] dark:border-darkGreenColor-dark',
                'bg-white dark:bg-transparent hover:bg-[#EDFCF5] dark:hover:bg-green-900/20',
                'flex items-center gap-1 transition-colors'
              )}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.00033 2.6665L8.00033 13.3332M13.3337 7.99984L2.66699 7.99984" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>{t('inventory.addProducts')}</span>
            </button>

            <div className="flex items-center gap-3">
              <ActionMenu
                menuId={`section-${section.id}`}
                openMenuId={openMenuId}
                onOpenChange={setOpenMenuId}
                items={[
                  {
                    label: t('inventory.editSection'),
                    icon: <svg width="20" height="20" viewBox="0 0 20 20" className='dark:text-[#CBD5E1] text-[#141B34]' fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15.8493 6.48826L11.4878 10.8498C10.6167 11.7209 10.1811 12.1564 9.65038 12.453C9.11963 12.7496 7.86637 13.048 6.66797 13.3333C6.9533 12.1349 7.2517 10.8817 7.54828 10.3509C7.84486 9.82018 8.28041 9.38463 9.15149 8.51354L13.513 4.15199L14.6812 2.98386C15.3263 2.33871 16.3723 2.33871 17.0174 2.98386C17.6626 3.629 17.6626 4.67498 17.0174 5.32013L15.8493 6.48826ZM13.513 4.15199L15.8493 6.48826" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M17.5 10C17.5 13.5355 17.5 15.3033 16.4016 16.4016C15.3033 17.5 13.5355 17.5 10 17.5C6.46447 17.5 4.6967 17.5 3.59835 16.4016C2.5 15.3033 2.5 13.5355 2.5 10C2.5 6.46447 2.5 4.6967 3.59835 3.59835C4.6967 2.5 6.46447 2.5 10 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>,
                    onClick: () => setEditingSection(section),
                  },
                  {
                    label: t('inventory.delete'),
                    icon: <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className='dark:text-[#F87171] text-[#D92D20]' xmlns="http://www.w3.org/2000/svg">
                      <path d="M16.25 4.5835L15.7336 12.9377C15.6016 15.0722 15.5356 16.1394 15.0006 16.9067C14.7361 17.2861 14.3955 17.6062 14.0006 17.8468C13.2018 18.3335 12.1325 18.3335 9.99395 18.3335C7.8526 18.3335 6.78192 18.3335 5.98254 17.8459C5.58733 17.6049 5.24666 17.2842 4.98224 16.9042C4.4474 16.1357 4.38288 15.0669 4.25384 12.9295L3.75 4.5835" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M2.5 4.58317H17.5M13.3798 4.58317L12.8109 3.40961C12.433 2.63005 12.244 2.24027 11.9181 1.99718C11.8458 1.94325 11.7692 1.89529 11.6892 1.85375C11.3283 1.6665 10.8951 1.6665 10.0288 1.6665C9.14069 1.6665 8.69665 1.6665 8.32974 1.8616C8.24842 1.90484 8.17082 1.95475 8.09774 2.01081C7.76803 2.26375 7.58386 2.6678 7.2155 3.47589L6.71077 4.58317" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M7.91602 13.75L7.91602 8.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M12.084 13.75L12.084 8.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>,
                    onClick: () => setDeletingSection(section),
                    destructive: true,
                  },
                ]}
              />
              <button
                type="button"
                onClick={() => toggleSection(section.id)}
                disabled={isSearching}
                className={cn('w-6 h-6 flex items-center justify-center', isSearching && 'opacity-30 cursor-not-allowed')}
                aria-label={isExpanded ? t('inventory.collapse') : t('inventory.expand')}
              >
                {isExpanded ? <svg width="24" height="24" className="rotate-180 transition duration-200 text-[#000000] dark:text-[#CBD5E1]" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                  : <svg width="24" height="24" className="transition duration-200 text-[#000000] dark:text-[#CBD5E1]" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                }
              </button>
            </div>
          </div>
        </div>

        {removalError?.sectionId === section.id && (
          <p role="alert" className="px-3 sm:px-5 pb-3 text-sm text-red-600 dark:text-red-400">
            {t('inventory.removeSelectedFailed', { count: removalError.count })}
          </p>
        )}
        {isExpanded && (
          <div className='px-3 sm:px-5 pb-3 sm:pb-4'>
            <div className="border-t border-[#EAECF0] dark:border-zinc-700">
              {products.length === 0 ? (
                <p className={cn(smallTextSize, tertiaryText, 'py-4 text-center')}>—</p>
              ) : (
                <div className="space-y-3 pt-4">
                  <ProductColumnsHeader hasCategories={sortedCategories.length > 0} t={t} />
                  {buckets.map((bucket) => {
                    const hasCategories = sortedCategories.length > 0;
                    const isCatCollapsed = hasCategories && categoryIsCollapsed(section.id, bucket.id);
                    return (
                      <div key={bucket.id ?? '__none__'}>
                        {hasCategories && (
                          <button
                            type="button"
                            onClick={() => toggleCategory(section.id, bucket.id)}
                            disabled={isSearching}
                            className={cn(
                              'w-full flex items-center justify-between gap-2 px-3 p-2 sm:py-2.5 rounded-lg bg-[#F9FAFB] dark:bg-[#19405E] mb-1 group transition-colors',
                              isSearching && 'cursor-default'
                            )}
                          >
                            <span className="text-[14px] sm:text-[16px] leading-5 font-semibold text-[#101828] dark:text-[#F8FAFC]">
                              {t('inventory.categoryProductCount', { name: bucket.name, count: bucket.products.length })}
                            </span>
                            {isCatCollapsed
                              ? <svg width="24" height="24" className="rotate-180 transition duration-200" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M18 15L12 9L6 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              :
                              <svg width="24" height="24" className="transition duration-200" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M18 15L12 9L6 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            }
                          </button>
                        )}
                        {!isCatCollapsed && (
                          <SortableList
                            items={bucket.products}
                            getId={(p) => p.id}
                            onReorder={(next) => handleReorderBucket(section.id, buckets, bucket.id, next)}
                          >
                            <ul>
                              {bucket.products.map((p) => (
                                <SortableProductRow
                                  key={p.id}
                                  product={p}
                                  selected={selectedProductIds.has(p.id)}
                                  onToggleSelected={() => toggleProductSelection(p.id)}
                                  sectionId={section.id}
                                  sortedCategories={sortedCategories}
                                  dragDisabled={isSearching}
                                  onSetCategory={setProductCategory}
                                  onSetMinimum={(minStockCount) => setProductMinimum(restaurantId, {
                                    productKey: p.productKey,
                                    minStockCount,
                                    inventorySectionProductId: p.id,
                                  })}
                                  onEditUnit={() => setEditingUnitFor({ sectionId: section.id, product: p })}
                                  onRename={() => setRenameFor({ sectionId: section.id, product: p })}
                                  onEditLink={() => setRelinkFor({
                                    inventorySectionProductId: p.id,
                                    productName: p.productName,
                                    sectionId: section.id,
                                    sectionName: section.sectionName,
                                    inventoryCategoryName: p.inventoryCategoryName,
                                    productKey: p.productKey,
                                  })}
                                  onSetPrice={() => setPriceFor({
                                    productKey: p.productKey,
                                    productName: p.productName,
                                    source: p.source,
                                    countUnit: p.countUnit,
                                    sectionName: section.sectionName,
                                    inventorySectionProductId: p.id,
                                  })}
                                  onRemove={() => removeProduct(p.id, section.id, restaurantId)}
                                  menuId={`product-${p.id}`}
                                  openMenuId={openMenuId}
                                  onOpenMenuChange={setOpenMenuId}
                                  t={t}
                                />
                              ))}
                            </ul>
                          </SortableList>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/inventory')}
              aria-label={t('inventory.back')}
            >
              <svg width="18" height="14" viewBox="0 0 18 14" className="text-[#0D0D12] dark:text-[#CBD5E1]" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0.900391 6.8999L16.9004 6.8999M6.90039 0.899902L0.900391 6.8999L6.90039 12.8999" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <h1 className={cn(primaryText, "text-[16px] sm:text-[24px] font-bold leading-7 sm:leading-[43px]")}>{t('inventory.manageSectionsTitle')}</h1>
          </div>
          <p className={cn(baseTextSize, secondaryText, 'font-normal leading-6 max-w-xl sm:block hidden')}>{t('inventory.manageSectionsSubtitle')}</p>
        </div>

        <div className="grid grid-cols-2 sm:flex items-center gap-2 flex-wrap">
          {canImport && (
            <button
              type="button"
              onClick={() => setImportOpen(true)}
              className={cn(
                smallTextSize, semiBoldText, secondaryText,
                'leading-5 rounded-xl text-center px-3 h-11 border border-[#D0D5DD] dark:border-[#28A878] dark:text-[#28A878]',
                'bg-white dark:bg-transparent hover:bg-gray-50',
                'transition-colors shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]'
              )}
            >
              <span>{t('inventory.import.button')}</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => setManageCategoriesOpen(true)}
            className={cn(
              smallTextSize, semiBoldText, secondaryText,
              'leading-5 rounded-xl text-center px-3 h-11 border border-[#D0D5DD] dark:border-[#28A878] dark:text-[#28A878]',
              'bg-white dark:bg-transparent hover:bg-gray-50',
              'transition-colors shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]'
            )}
          >
            {t('inventory.manageCategories')}
          </button>
          <button
            type="button"
            onClick={() => setCreateGroupOpen(true)}
            className={cn(
              smallTextSize, 'text-white text-center dark:text-white', semiBoldText,
              'leading-5 bg-[#0E8965] dark:bg-[#0E8965] hover:bg-[#0E8965]/95 rounded-xl px-3 h-11',
              'flex items-center justify-center gap-1 transition-colors shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]'
            )}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 3.33398V16.6673" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M3.33398 10H16.6673" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>{t('inventory.newGroup')}</span>
          </button>
        </div>
      </div>

      {/* Find a product without opening every section by hand. */}
      <div className="mb-4 sm:mb-6">
        <InventoryProductSearchInput
          value={search}
          onChange={setSearch}
          placeholder={t('inventory.searchProductsAcrossSections')}
          className="md:w-[320px]"
        />
      </div>

      {/* Groups → sections */}
      {isLoadingSections && groups.length === 0 ? (
        <p className={cn(baseTextSize, tertiaryText, 'text-center py-12')}>{t('inventory.loading')}</p>
      ) : sortedGroups.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#D0D5DD] dark:border-zinc-700 bg-white dark:bg-zinc-900 p-12 text-center">
          <p className={cn(baseTextSize, tertiaryText)}>{t('inventory.noGroups')}</p>
        </div>
      ) : visibleGroups.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#D0D5DD] dark:border-zinc-700 bg-white dark:bg-zinc-900 p-12 text-center">
          <p className={cn(baseTextSize, tertiaryText)}>
            {sectionsAwaitingProducts
              ? t('inventory.loading')
              : t('inventory.noSearchResults', { query: search.trim() })}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {visibleGroups.map((group) => {
            const groupSections = sectionsForGroup(group.id);
            const totalProducts = productCountForGroup(group.id);
            const isGroupCollapsed = groupIsCollapsed(group.id);
            return (
              <div
                key={group.id}
                className="rounded-[16px] p-3 sm:p-4 border border-[#E8E8E9] dark:border-[#1E3A4E] shadow-[0px_1px_1.5px_0.1px_#16192408] dark:shadow-[0px_1px_1.5px_0.1px_#00000040]"
              >
                {/* Group header bar */}
                <div
                  className={cn(
                    'rounded-lg flex sm:flex-row flex-col items-start sm:items-center justify-between gap-2 sm:gap-4 p-3 bg-[#0E89650D] dark:bg-[#28A8781A]',
                    !isSearching && 'cursor-pointer'
                  )}
                  onClick={() => toggleGroup(group.id)}
                >
                  <div className='w-full flex items-center gap-3 justify-between'>
                    <div className="min-w-0 flex items-center flex-row gap-3">
                      <span className="text-[14px] sm:text-[16px] leading-5 font-semibold text-[#101828] dark:text-[#F8FAFC]">{group.name}</span>
                      <span className="text-[14px] leading-5 font-medium text-[#667085] dark:text-[#CBD5E1] sm:block hidden">
                        {isSearching
                          ? t('inventory.groupSearchSummary', { sections: groupSections.length, matches: totalProducts })
                          : t('inventory.groupSummary', { sections: groupSections.length, products: totalProducts })}
                      </span>
                    </div>

                    <div className="flex items-center gap-1 sm:gap-5 shrink-0">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setCreateSectionForGroupId(group.id); }}
                        className={cn(smallTextSize, darkGreenTextColor, 'dark:text-[#28A878] leading-5 font-medium flex items-center gap-1 transition-colors')}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8.00033 2.6665L8.00033 13.3332M13.3337 7.99984L2.66699 7.99984" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span className="underline">{t('inventory.addSection')}</span>
                      </button>

                      <ActionMenu
                        menuId={`group-${group.id}`}
                        openMenuId={openMenuId}
                        onOpenChange={setOpenMenuId}
                        items={[
                          {
                            label: t('inventory.editGroup'),
                            icon: <svg width="20" height="20" viewBox="0 0 20 20" className='dark:text-[#CBD5E1] text-[#141B34]' fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M15.8493 6.48826L11.4878 10.8498C10.6167 11.7209 10.1811 12.1564 9.65038 12.453C9.11963 12.7496 7.86637 13.048 6.66797 13.3333C6.9533 12.1349 7.2517 10.8817 7.54828 10.3509C7.84486 9.82018 8.28041 9.38463 9.15149 8.51354L13.513 4.15199L14.6812 2.98386C15.3263 2.33871 16.3723 2.33871 17.0174 2.98386C17.6626 3.629 17.6626 4.67498 17.0174 5.32013L15.8493 6.48826ZM13.513 4.15199L15.8493 6.48826" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M17.5 10C17.5 13.5355 17.5 15.3033 16.4016 16.4016C15.3033 17.5 13.5355 17.5 10 17.5C6.46447 17.5 4.6967 17.5 3.59835 16.4016C2.5 15.3033 2.5 13.5355 2.5 10C2.5 6.46447 2.5 4.6967 3.59835 3.59835C4.6967 2.5 6.46447 2.5 10 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>,
                            onClick: () => setEditingGroup(group),
                          },
                          {
                            label: t('inventory.deleteGroup'),
                            icon: <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className='dark:text-[#F87171] text-[#D92D20]' xmlns="http://www.w3.org/2000/svg">
                              <path d="M16.25 4.5835L15.7336 12.9377C15.6016 15.0722 15.5356 16.1394 15.0006 16.9067C14.7361 17.2861 14.3955 17.6062 14.0006 17.8468C13.2018 18.3335 12.1325 18.3335 9.99395 18.3335C7.8526 18.3335 6.78192 18.3335 5.98254 17.8459C5.58733 17.6049 5.24666 17.2842 4.98224 16.9042C4.4474 16.1357 4.38288 15.0669 4.25384 12.9295L3.75 4.5835" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                              <path d="M2.5 4.58317H17.5M13.3798 4.58317L12.8109 3.40961C12.433 2.63005 12.244 2.24027 11.9181 1.99718C11.8458 1.94325 11.7692 1.89529 11.6892 1.85375C11.3283 1.6665 10.8951 1.6665 10.0288 1.6665C9.14069 1.6665 8.69665 1.6665 8.32974 1.8616C8.24842 1.90484 8.17082 1.95475 8.09774 2.01081C7.76803 2.26375 7.58386 2.6678 7.2155 3.47589L6.71077 4.58317" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                              <path d="M7.91602 13.75L7.91602 8.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                              <path d="M12.084 13.75L12.084 8.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>,
                            onClick: () => setDeletingGroup(group),
                            destructive: true,
                            disabled: groupSections.length > 0,
                          },
                        ]}
                      />

                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleGroup(group.id); }}
                        disabled={isSearching}
                        className={cn('w-6 h-6 flex items-center justify-center', isSearching && 'opacity-30 cursor-not-allowed')}
                        aria-label={isGroupCollapsed ? t('inventory.expand') : t('inventory.collapse')}
                      >
                        <svg
                          width="24"
                          height="24"
                          className={cn('transition duration-200 text-[#000000] dark:text-[#CBD5E1]', !isGroupCollapsed && 'rotate-180')}
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <span className="text-[14px] leading-5 font-medium text-[#667085] dark:text-[#CBD5E1] block sm:hidden">
                    {isSearching
                      ? t('inventory.groupSearchSummary', { sections: groupSections.length, matches: totalProducts })
                      : t('inventory.groupSummary', { sections: groupSections.length, products: totalProducts })}
                  </span>
                </div>

                {/* Sections in this group */}
                {!isGroupCollapsed && (
                  <div className="mt-3">
                    {groupSections.length === 0 ? (
                      <p className={cn(smallTextSize, tertiaryText, 'text-center py-6')}>{t('inventory.noSectionsInGroup')}</p>
                    ) : (
                      <div className="space-y-3 sm:space-y-5">
                        {groupSections.map(renderSection)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      <DeleteConfirmationModal
        isOpen={removingFrom !== null}
        title={t('inventory.removeSelectedFromSection', { count: removingFrom?.productIds.length ?? 0 })}
        message={t('inventory.confirmRemoveSelected', {
          count: removingFrom?.productIds.length ?? 0,
          name: removingFrom?.section.sectionName ?? '',
        })}
        onConfirm={handleRemoveSelected}
        onCancel={() => setRemovingFrom(null)}
        isBusy={isRemoving}
        confirmLabel={isRemoving ? t('inventory.removingProducts') : t('inventory.removeFromSection')}
        cancelLabel={t('inventory.cancel')}
      />
      <SaveInventoryGroupModal
        isOpen={createGroupOpen || editingGroup !== null}
        onClose={() => { setCreateGroupOpen(false); setEditingGroup(null); }}
        mode={editingGroup ? 'edit' : 'create'}
        restaurantId={restaurantId}
        group={editingGroup || undefined}
      />
      <SaveInventorySectionModal
        isOpen={createSectionForGroupId !== null || editingSection !== null}
        onClose={() => { setCreateSectionForGroupId(null); setEditingSection(null); }}
        mode={editingSection ? 'edit' : 'create'}
        restaurantId={restaurantId}
        groupId={createSectionForGroupId || undefined}
        section={editingSection || undefined}
      />
      <AssignProductsModal
        isOpen={assignTo !== null}
        onClose={() => { setAssignTo(null); }}
        restaurantId={restaurantId}
        sectionId={assignTo?.id || ''}
        sectionName={assignTo?.sectionName || ''}
        allowMultipleSections
      />
      <EditProductUnitModal
        isOpen={editingUnitFor !== null}
        onClose={() => setEditingUnitFor(null)}
        sectionId={editingUnitFor?.sectionId || ''}
        product={editingUnitFor?.product || null}
      />
      <ReLinkInventoryProductModal
        product={relinkFor}
        restaurantId={restaurantId}
        onClose={() => setRelinkFor(null)}
      />
      <RenameInventoryProductModal
        target={renameFor}
        onClose={() => setRenameFor(null)}
      />
      <ManageInventoryCategoriesModal
        isOpen={manageCategoriesOpen}
        onClose={() => setManageCategoriesOpen(false)}
        restaurantId={restaurantId}
      />
      <ImportInventoryExcelModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        restaurantId={restaurantId}
        onCommitted={() => loadSections(restaurantId)}
      />
      <SetInventoryPriceModal
        product={priceFor}
        restaurantId={restaurantId}
        onClose={() => setPriceFor(null)}
      />
      <DeleteConfirmationModal
        isOpen={deletingSection !== null}
        title={t('inventory.confirmDelete')}
        message={t('inventory.confirmDeleteMessage', { name: deletingSection?.sectionName || '' })}
        onConfirm={handleDeleteSection}
        onCancel={() => setDeletingSection(null)}
        confirmLabel={t('inventory.delete')}
        cancelLabel={t('inventory.cancel')}
      />
      <DeleteConfirmationModal
        isOpen={deletingGroup !== null}
        title={t('inventory.confirmDeleteGroup')}
        message={t('inventory.confirmDeleteGroupMessage', { name: deletingGroup?.name || '' })}
        onConfirm={handleDeleteGroup}
        onCancel={() => setDeletingGroup(null)}
        confirmLabel={t('inventory.delete')}
        cancelLabel={t('inventory.cancel')}
      />
    </div>
  );
};

export default InventoryEditPage;
